import {
    ContextManagerStorage,
    StorableContext,
    ContextSizeMap,
    RuntimeName
} from '../../types';
import { CONTEXT_MANAGER_STORAGE_SCRIPT } from '../../constants';
import { ContextDoesNotExist, ContextAlreadyExists, InvalidStorableContext } from '../../contextErrors';
import { isObject } from '../../helpers';

/**
 * The storage key to use when saving context names which are designated to the browser.
 */
const EXPOSE_TO_BROWSER_KEY = '__exposeToBrowser';

/**
 * The Base context manager.
 *
 * @class
 */
export abstract class BaseContextManager {

    /**
     * The Runtime name of the current implementing manager.
     */
    abstract runtimeName: RuntimeName;

    /**
     * Returns current available storage
     */
    protected abstract get storage(): ContextManagerStorage;

    /**
     * Creates the root storage to be available through the given function execution lifetime.
     * Relevant only on Server mode.
     *
     * @param _exposeTo
     */
    abstract run<T>(_exposeTo: (...args: any[]) => T): T;

    /**
     * Creates a new scoped context within current manager storage.
     *
     * @param contextName - The context name to set.
     * @param context - The context value to expose under the given name.
     * @param exposeToBrowser - Rather to expose this context to the browser markup or not.
     */
    with(contextName: string, context: StorableContext, exposeToBrowser = false) {
        if (this.storage[contextName]) {
            throw new ContextAlreadyExists(contextName);
        }

        if (!isObject(context)) {
            throw new InvalidStorableContext(contextName);
        }

        if (exposeToBrowser) {
            this.storage[EXPOSE_TO_BROWSER_KEY] ||= [];
            this.storage[EXPOSE_TO_BROWSER_KEY].push(contextName);
        }

        this.storage[contextName] = Object.seal(context);
    }

    /**
     * Returns requested context from the current available storage.
     *
     * @param contextName - The context name to get.
     * @throws {ContextDoesNotExist} if request context is not present.
     */
    get<T = StorableContext>(contextName: string): T {
        const context = this.storage[contextName];
        if (!context) {
            throw new ContextDoesNotExist(contextName);
        }

        return context as T;
    }

    /**
     * Checks if a given context is present, if nothing is provider result is based on general storage presence.
     *
     * @param contextName - The optional context name to check if present.
     */
    exists(contextName?: string): boolean {
        try {
            if (!this.storage) {
                return false;
            }

            return contextName ? !!this.storage[contextName] : true;
        } catch {
            return false;
        }
    }

    /**
     * Returns a map of context name to context data chars length.
     */
    sizes(): ContextSizeMap {
        return Object.entries(this.storage).reduce((sizeMap, [contextName, context]) => {
            try {
                if (contextName === EXPOSE_TO_BROWSER_KEY) {
                    return sizeMap;
                }

                return {
                    ...sizeMap,
                    [contextName]: JSON.stringify(context).length
                };
            } catch (error: unknown) {
                // JSON.stringify always throw an error object with a message
                const prefix = 'Error while trying to calculate context sizes';
                if (error instanceof Error) {
                    error.message = `${prefix}: ${error.message}`;
                    throw error;
                }

                throw new Error(prefix);
            }
        }, {});
    }

    /**
     * Stringify ContextManager available storage.
     *
     * @param contextNames - The selected keys to extract
     */
    toString(contextNames?: string[]): string {
        const storage = this.storage || {};

        if (!contextNames) {
            return JSON.stringify(storage);
        }

        return JSON.stringify(contextNames.reduce((filteredStorage, contextName) => ({
            ...filteredStorage,
            [contextName]: storage[contextName]
        }), {}));
    }

    /**
     * Returns current storage as HTML markup which may be used to expose any browser context.
     */
    markup(): string {
        const exposedNames = this.storage[EXPOSE_TO_BROWSER_KEY] ?? [];

        return (
            `<script type="application/json" id="${CONTEXT_MANAGER_STORAGE_SCRIPT}">
                ${this.toString(exposedNames as string[])}
            </script>`
        ).trim();
    }
}
